import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { translate, TranslocoService } from '@jsverse/transloco';
import { filter, Subject, takeUntil } from 'rxjs';
import { DropdownObject, EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';

import { Collection } from 'app/providers/storage-api/collection.service';
import { ContentChannel } from 'app/providers/storage-api/content-channel.service';
import { Project } from 'app/providers/storage-api/project.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { nameof, Utils } from 'app/utils/utils';
import { ExtractFormControl } from 'app/utils/validator.utils';
import { generalNameValidator } from '../../../../utils/customValidators';
import {
  CollectionConfigurationService,
  NO_SECTION_SEPARATOR_ID,
} from './collection-configuration.service';
import { ContentChannelLabelPipe } from './content-channel-label.pipe';

type CollectionCreationFollowUp = 'empty' | 'upload';

export interface UploadCreateDialogResult {
  collectionName: string;
  displayName: string;
  showUpload: boolean;
}

interface Form {
  name: FormControl<string>;
  followUp: FormControl<CollectionCreationFollowUp>;
  notes: FormControl<string>;
  contentChannel1: FormControl<ContentChannel>;
  contentChannel2: FormControl<ContentChannel>;
  contentChannel3: FormControl<ContentChannel>;
  sectionSeparator: FormControl<string>;
  focusedSpeaker: FormControl<string>;
}

export type CollectionFormValue = ExtractFormControl<Form>;

export interface SectionField {
  label: string;
  value: string;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-upload-create',
  imports: [AppSharedModule, ContentChannelLabelPipe],
  providers: [CollectionConfigurationService],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './upload-create.component.html',
  styleUrls: ['./upload-create.component.scss'],
})
export class UploadCreateDialogComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() project!: Project;
  @Input() collection?: Collection;

  @Output() close = new EventEmitter<UploadCreateDialogResult | null>();

  isLoading = false;
  isSaving = false;
  hasChanges = false;

  form: FormGroup<Form>;
  collectionNameStatus: ErrorObject = { state: EnumErrorStates.SUCCESS, text: '' };
  existingCollectionNames: string[];
  originalFormValue: CollectionFormValue;

  contentChannels: ContentChannel[];

  sectionFields: SectionField[];
  focusedSpeakerFields: SectionField[];
  sectionFieldOptions: DropdownObject[] = [];
  focusedSpeakerFieldOptions: DropdownObject[] = [];

  activeLang: string;

  private readonly _destroy$ = new Subject<void>();

  private readonly _formBuilder = inject(FormBuilder);
  private readonly _collectionConfigurationService = inject(CollectionConfigurationService);
  private readonly _translocoService = inject(TranslocoService);
  private readonly _alertService = inject(AlertService);

  constructor() {}

  async ngOnInit() {
    this.isLoading = true;

    this.buildForm();

    if (this.shouldShowSectionSeparator) {
      this.form.controls.sectionSeparator.valueChanges
        .pipe(takeUntil(this._destroy$))
        .subscribe((sectionSeparator) => {
          this.initFocusSeparator(sectionSeparator);
        });

      this.initSectionSeparator();
    }

    await this.getContentChannels();

    this.activeLang = this._translocoService.getActiveLang();
    this.existingCollectionNames = CollectionConfigurationService.getExistingCollectionNames(
      this.project,
      this.collection
    );
    this.originalFormValue = this.form.getRawValue();

    this.isLoading = false;
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  onCancel(): void {
    this.close.emit(null);
  }

  async onConfirm(): Promise<void> {
    this.isSaving = true;

    try {
      const result = await this._collectionConfigurationService.save(
        this.project.id,
        this.form.getRawValue(),
        this.collection
      );

      this._alertService.showToast(
        translate('upload-create.success-collection-save', { isNew: !this.isEditMode })
      );

      this.close.emit(result);
    } catch (error) {
      this._alertService.showErrorToast(
        translate('upload-create.failed-collection-save', { isNew: !this.isEditMode }),
        error
      );
    } finally {
      this.isSaving = false;
    }
  }

  onSelectContentChannel1(contentChannel: ContentChannel): void {
    this.form.controls.contentChannel1.setValue(contentChannel);

    this.onSelectContentChannel2(contentChannel.children?.[0]);
  }

  onSelectContentChannel2(contentChannel: ContentChannel): void {
    this.form.controls.contentChannel2.setValue(contentChannel);

    this.onSelectContentChannel3(contentChannel.children?.[0]);
  }

  onSelectContentChannel3(contentChannel: ContentChannel): void {
    this.form.controls.contentChannel3.setValue(contentChannel);
  }

  onSelectSectionSeparator(sectionSeparator: string): void {
    this.form.controls.sectionSeparator.setValue(sectionSeparator);
  }

  onSelectFocusedSpeakerSeparator(focusedSpeaker: string): void {
    this.form.controls.focusedSpeaker.setValue(focusedSpeaker);
  }

  get contentChannel1(): ContentChannel {
    return this.form.controls.contentChannel1.value;
  }

  get contentChannel2(): ContentChannel {
    return this.form.controls.contentChannel2.value;
  }

  get contentChannel3(): ContentChannel {
    return this.form.controls.contentChannel3.value;
  }

  get isEditMode(): boolean {
    return !!this.collection;
  }

  get shouldShowSectionSeparator(): boolean {
    return this.collection?.hasSections && !!this.collection?.sectionFieldNames.length;
  }

  get shouldShowFocusedSpeakerSeparator(): boolean {
    const selectedChannel = this.form.controls.contentChannel1.value;
    const selectedSubChannel = this.form.controls.contentChannel2.value;

    const selectedChannelLabel = selectedChannel?.label.trim().toLocaleLowerCase();
    const selectedSubChannelLabel = selectedSubChannel?.label.trim().toLocaleLowerCase();

    return (
      this.isEditMode &&
      selectedChannelLabel === 'conversational' &&
      !selectedSubChannelLabel?.includes('summary')
    );
  }

  get sectionSeparator(): string {
    return this.form.controls.sectionSeparator.value;
  }

  get focusSeparator(): string {
    return this.form.controls.focusedSpeaker.value;
  }

  get isConfirmDisabled(): boolean {
    return this.form.invalid || this.isSaving || !this.hasChanges;
  }

  private buildForm(): void {
    this.form = this._formBuilder.group<Form>({
      name: new FormControl('', Validators.required),
      followUp: new FormControl('upload'),
      notes: new FormControl(''),
      contentChannel1: new FormControl(null),
      contentChannel2: new FormControl(null),
      contentChannel3: new FormControl(null),
      sectionSeparator: new FormControl(NO_SECTION_SEPARATOR_ID),
      focusedSpeaker: new FormControl(NO_SECTION_SEPARATOR_ID),
    });

    this.focusedSpeakerFields = [
      { label: translate('upload-create.no-focus-separator'), value: NO_SECTION_SEPARATOR_ID },
    ];
    this.focusedSpeakerFieldOptions = this.focusedSpeakerFields.map((field) => ({
      title: field.label,
      id: field.value,
    }));

    if (this.isEditMode) {
      this.form.patchValue({
        name: this.collection.displayName,
        notes: this.collection.notes,
        sectionSeparator: this.collection.sectionSeparator?.label ?? NO_SECTION_SEPARATOR_ID,
        focusedSpeaker: this.collection.focusedSpeakerName ?? NO_SECTION_SEPARATOR_ID,
      });
    }

    this.form.controls.name.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((name) => {
      generalNameValidator(
        name,
        this.collectionNameStatus,
        translate('collection-upload.collection'),
        64,
        true,
        this.existingCollectionNames,
        false
      );

      if (this.collectionNameStatus.state === EnumErrorStates.ERROR) {
        this.form.controls.name.setErrors({ incorrect: true }, { emitEvent: false });
      } else {
        this.form.controls.name.setErrors(null, { emitEvent: false });
      }
    });

    this.form.valueChanges
      .pipe(
        filter(() => !!this.originalFormValue),
        takeUntil(this._destroy$)
      )
      .subscribe(
        () =>
          (this.hasChanges = !Utils.deepEquals(this.originalFormValue, this.form.getRawValue(), [
            nameof<ContentChannel>('children'),
            nameof<ContentChannel>('properties'),
            nameof<ContentChannel>('label'),
          ]))
      );
  }

  private async getContentChannels(): Promise<void> {
    try {
      const contentChannelsForUi =
        await this._collectionConfigurationService.getContentChannelsForUi(this.collection);

      this.contentChannels = contentChannelsForUi.contentChannels;

      this.form.patchValue({
        contentChannel1: contentChannelsForUi.node1,
        contentChannel2: contentChannelsForUi.node2,
        contentChannel3: contentChannelsForUi.node3,
      });
    } catch (error) {
      this._alertService.showErrorToast(
        translate('upload-create.failed-content-channel-loading'),
        error
      );
    }
  }

  private initSectionSeparator(): void {
    this.sectionFields = CollectionConfigurationService.createSectionFields(this.collection);
    this.sectionFieldOptions = this.sectionFields.map((field) => ({
      title: field.label,
      id: field.value,
    }));

    this.form.patchValue(
      {
        sectionSeparator: this.collection.sectionSeparator?.label ?? NO_SECTION_SEPARATOR_ID,
        focusedSpeaker: this.collection.focusedSpeakerName ?? NO_SECTION_SEPARATOR_ID,
      },
      { emitEvent: false }
    );

    this.initFocusSeparator(this.form.controls.sectionSeparator.value);
  }

  private async initFocusSeparator(sectionSeparator?: string): Promise<void> {
    if (!sectionSeparator || sectionSeparator === NO_SECTION_SEPARATOR_ID) {
      this.resetFocusedSpeaker();
      return;
    }

    await this.setFocusedSpeaker(sectionSeparator);
  }

  private resetFocusedSpeaker(): void {
    const noFocusedSpeakerOption = this.createNoFocusedSpeakerOption();

    this.focusedSpeakerFields = [noFocusedSpeakerOption];
    this.focusedSpeakerFieldOptions = this.focusedSpeakerFields.map((field) => ({
      title: field.label,
      id: field.value,
    }));

    this.form.patchValue(
      {
        focusedSpeaker: NO_SECTION_SEPARATOR_ID,
      },
      { emitEvent: false }
    );
  }

  private async setFocusedSpeaker(sectionSeparator: string): Promise<void> {
    this.focusedSpeakerFields = await this._collectionConfigurationService.createFocusSectionFields(
      this.collection,
      sectionSeparator,
      this.project.id
    );

    if (!this.focusedSpeakerFields.length) {
      this.focusedSpeakerFields = [this.createNoFocusedSpeakerOption()];
    }

    this.focusedSpeakerFieldOptions = this.focusedSpeakerFields.map((field) => ({
      title: field.label,
      id: field.value,
    }));

    const optionIds = new Set(this.focusedSpeakerFieldOptions.map((option) => option.id));
    if (!optionIds.has(this.focusSeparator)) {
      const fallbackSelection = optionIds.has(sectionSeparator)
        ? sectionSeparator
        : NO_SECTION_SEPARATOR_ID;

      this.form.patchValue({
        focusedSpeaker: fallbackSelection,
      });
    }
  }

  private createNoFocusedSpeakerOption(): SectionField {
    return {
      label: translate('upload-create.no-focus-separator'),
      value: NO_SECTION_SEPARATOR_ID,
    };
  }
}
